import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { Checkbox, PageHeader, SectionHeader, StepActions } from "@components/ui_v2";
import { BarTotal, ButtonAddService, CardService, ProfitSummary, RowExpense } from "@components/ui_v2/incomeProfile";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useLazyGetCuentasByIdQuery, usePatchCuentaMutation } from "@api/account";
import { usePostValidateNextStageMutation } from "@api/applications";
import {
  useDeleteServiceMonthlyIncomeMutation,
  useLazyGetServiceMonthlyIncomeCatalogQuery,
  useLazyGetServiceMonthlyIncomeListQuery,
  useLazyGetServiceMonthlyOutcomeCatalogQuery,
  useLazyGetServiceMonthlyOutcomeListQuery,
  usePatchServiceMonthlyIncomeMutation,
  usePatchServiceMonthlyOutcomeMutation,
  usePostServiceMonthlyIncomeMutation,
  usePostServiceMonthlyOutcomeMutation,
} from "@api/serviceMonthly";
import useStatusConfig from "@hooks/useStatusConfig";
import {
  CUSTOM_SERVICE_CATEGORY,
  getServiceTotal,
  toIncomePayload,
  toNumber,
  toThousands,
} from "@helpers/incomeProfileHelpers";
import { formatCurrencyWhole } from "@helpers/funciones";
import { PATHS_TOFU_V2, SERVICE_MONTHLY_SEGMENT, STATUS_CATALOG } from "@helpers/constantes";
import { Expense, IncomeService } from "@interfaces/incomeProfile";
import { ServiceMonthlyCatalogItem, ServiceMonthlyIncome, ServiceMonthlyOutcome } from "@interfaces/serviceMonthly";
import { ApiError } from "@interfaces/response";

interface FormValues {
  services: IncomeService[];
  expenses: Expense[];
  declara: boolean;
}

const validationSchema = yup.object().shape({
  declara: yup.boolean().oneOf([true]),
  services: yup.array().of(
    yup.object().shape({
      // "Otro servicio" exige el nombre (`other_service_name`).
      customName: yup.string().when("category", {
        is: CUSTOM_SERVICE_CATEGORY,
        then: (schema) => schema.trim().required("Campo requerido"),
      }),
    }),
  ),
});

/** Bloque vacío para un servicio del catálogo: 20 días al mes por default, como en el diseño. */
const emptyService = (id: number, category: string): IncomeService => ({
  id,
  category,
  customName: "",
  perDay: 0,
  daysPerMonth: 20,
  perMonth: 0,
  price: "",
});

/** Registro del API → bloque de la pantalla. `WEEK` (sin fórmula definida aún) se trata como mensual. */
const fromIncomeRecord = (
  record: ServiceMonthlyIncome,
  id: number,
  catalog: ServiceMonthlyCatalogItem[],
): IncomeService => {
  const category = catalog.find((item) => item.id === record.service_monthly_income_catalog_id)?.code ?? "";
  const byDay = record.period_type === "DAY";
  return {
    id,
    serverId: record.id,
    category,
    customName: record.other_service_name ?? "",
    perDay: byDay ? record.average_quantity : 0,
    daysPerMonth: byDay ? (record.days_per_month ?? 20) : 20,
    perMonth: byDay ? 0 : record.average_quantity,
    price: toThousands(String(Math.round(record.price))),
  };
};

/** Un renglón por concepto del catálogo de egresos, con el monto guardado si ya existe. */
const toExpenses = (catalog: ServiceMonthlyCatalogItem[], saved: ServiceMonthlyOutcome[]): Expense[] =>
  catalog.map((concept) => {
    const record = saved.find((item) => item.service_monthly_outcome_catalog_id === concept.id);
    return {
      id: concept.id,
      label: concept.name,
      note: concept.description ?? "",
      serverId: record?.id,
      amount: record ? toThousands(String(Math.round(record.amount))) : "",
    };
  });

/**
 * `MC_PerfilIngresos` del diseño de Mercader: sustituye el formato en papel FO-PR-45 "Solicitud de
 * Ingresos y Egresos Perfil Médico". Ingresos por servicio, egresos mensuales, utilidad con margen y
 * declaración bajo protesta (status CSPIE). Servicios: `api/serviceMonthly` (catálogos, ingresos y egresos
 * de la cuenta) y `patchCuenta` para la declaración. Totales, utilidad y margen los calcula el front.
 */
export const PerfilIngresosMercader = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus, previousStatus } = useStatusConfig(STATUS_CATALOG.perfil_ingresos.code);

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const clientDetailsId = useSelector((state: RootState) => state.register.personal_data.id);

  const [getIncomeCatalog] = useLazyGetServiceMonthlyIncomeCatalogQuery();
  const [getOutcomeCatalog] = useLazyGetServiceMonthlyOutcomeCatalogQuery();
  const [getIncomeList] = useLazyGetServiceMonthlyIncomeListQuery();
  const [getOutcomeList] = useLazyGetServiceMonthlyOutcomeListQuery();
  const [postIncome] = usePostServiceMonthlyIncomeMutation();
  const [patchIncome] = usePatchServiceMonthlyIncomeMutation();
  const [deleteIncome] = useDeleteServiceMonthlyIncomeMutation();
  const [postOutcome] = usePostServiceMonthlyOutcomeMutation();
  const [patchOutcome] = usePatchServiceMonthlyOutcomeMutation();
  const [getAccount] = useLazyGetCuentasByIdQuery();
  const [patchAccount] = usePatchCuentaMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [incomeCatalog, setIncomeCatalog] = useState<ServiceMonthlyCatalogItem[]>([]);
  const nextId = useRef(1);
  /** Ingresos guardados al abrir la pantalla: los que ya no estén al continuar se borran. */
  const savedIncomeIds = useRef<string[]>([]);

  const catalogIdOf = (code: string) => incomeCatalog.find((item) => item.code === code)?.id ?? "";

  /** Ingresos: borra los bloques quitados, actualiza los guardados y crea los nuevos. */
  const saveIncomes = async (services: IncomeService[]) => {
    const keptIds = services.map((service) => service.serverId).filter(Boolean);
    const removed = savedIncomeIds.current.filter((id) => !keptIds.includes(id));
    await Promise.all(removed.map((id) => deleteIncome({ clientDetailsId, id }).unwrap()));

    await Promise.all(
      services.map((service) => {
        const body = toIncomePayload(service, catalogIdOf(service.category));
        return service.serverId
          ? patchIncome({ clientDetailsId, id: service.serverId, body }).unwrap()
          : postIncome({ clientDetailsId, body }).unwrap();
      }),
    );
  };

  /** Un egreso por concepto: PATCH si ya existe; POST si se capturó algo (un concepto vacío no se crea). */
  const saveOutcomes = async (expenses: Expense[]) => {
    await Promise.all(
      expenses.map(async (expense) => {
        const amount = toNumber(expense.amount);
        if (expense.serverId) {
          await patchOutcome({ clientDetailsId, id: expense.serverId, body: { amount } }).unwrap();
          return;
        }
        if (!expense.amount) return;
        try {
          await postOutcome({
            clientDetailsId,
            body: { service_monthly_outcome_catalog_id: expense.id, amount },
          }).unwrap();
        } catch (error) {
          // Concepto ya capturado: el API responde 400 con el id existente; se actualiza ese registro.
          const existingId = (error as { data?: { data?: { id?: string } } })?.data?.data?.id;
          if (!existingId) throw error;
          await patchOutcome({ clientDetailsId, id: existingId, body: { amount } }).unwrap();
        }
      }),
    );
  };

  const formik = useFormik<FormValues>({
    initialValues: { services: [], expenses: [], declara: false },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        await saveIncomes(values.services);
        await saveOutcomes(values.expenses);
        await patchAccount({ accountId: clientDetailsId, body: { income_declaration_accepted: true } }).unwrap();

        // `onQueryStarted` de `postValidateNextStage` ya actualiza `loan_status`; no se despacha a mano.
        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        navigate(nextStatus.url);
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "PerfilIngresosMercader-submit", values } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, setFieldValue, setValues } = formik;

  /**
   * Carga inicial: catálogos, lo guardado en la cuenta y la declaración. Sin catálogos no hay qué pintar,
   * así que su falla abre el modal de error; las listas guardadas y la cuenta son con respaldo (Sentry).
   */
  const getInitialData = useCallback(async () => {
    setIsLoadingInitial(true);
    try {
      const segment = SERVICE_MONTHLY_SEGMENT.medico;
      const [incomes, outcomes] = await Promise.all([
        getIncomeCatalog({ segment }).unwrap(),
        getOutcomeCatalog({ segment }).unwrap(),
      ]);
      setIncomeCatalog(incomes);

      const [incomeList, outcomeList, account] = await Promise.allSettled([
        getIncomeList({ clientDetailsId }).unwrap(),
        getOutcomeList({ clientDetailsId }).unwrap(),
        getAccount({ id: clientDetailsId }).unwrap(),
      ]);
      [incomeList, outcomeList, account].forEach((result) => {
        if (result.status === "rejected") {
          Sentry.captureException(result.reason, { extra: { component: "PerfilIngresosMercader-saved_data" } });
        }
      });

      const savedIncomes = incomeList.status === "fulfilled" ? incomeList.value : [];
      savedIncomeIds.current = savedIncomes.map((record) => record.id);
      const services = savedIncomes.length
        ? savedIncomes.map((record) => fromIncomeRecord(record, nextId.current++, incomes))
        : [emptyService(nextId.current++, incomes[0]?.code ?? "")];

      await setValues(
        {
          services,
          expenses: toExpenses(outcomes, outcomeList.status === "fulfilled" ? outcomeList.value : []),
          declara: account.status === "fulfilled" ? Boolean(account.value?.data?.income_declaration_accepted) : false,
        },
        true,
      );
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "PerfilIngresosMercader-catalogs" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoadingInitial(false);
    }
  }, [
    clientDetailsId,
    getIncomeCatalog,
    getOutcomeCatalog,
    getIncomeList,
    getOutcomeList,
    getAccount,
    setValues,
    dispatch,
  ]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const updateService = (index: number, patch: Partial<IncomeService>) =>
    setFieldValue(`services[${index}]`, { ...values.services[index], ...patch });

  const addService = () =>
    setFieldValue("services", [...values.services, emptyService(nextId.current++, incomeCatalog[0]?.code ?? "")]);

  const removeService = (id: number) =>
    setFieldValue(
      "services",
      values.services.filter((service) => service.id !== id),
    );

  const categories = incomeCatalog.map((item) => ({ code: item.code, name: item.name }));
  const totalIncome = values.services.reduce((acc, service) => acc + getServiceTotal(service), 0);
  const totalExpenses = values.expenses.reduce((acc, expense) => acc + toNumber(expense.amount), 0);
  // Cada bloque se guarda como un ingreso: uno sin volumen o sin precio no se puede enviar.
  const hasIncompleteService = values.services.some((service) => getServiceTotal(service) <= 0);
  const bloqueado = isLoadingInitial || formik.isSubmitting;

  return (
    <Box>
      <PageHeader
        eyebrow="Ingresos y egresos"
        title="Perfil de ingresos y egresos"
        subtitle="Registra los ingresos de tu práctica por tipo de servicio y tus gastos mensuales. Con esa información calculamos tu utilidad y verificamos que la renta del equipo sea acorde a tu capacidad de pago. Sustituye el formato que antes se llenaba y firmaba en papel."
      />

      {isLoadingInitial ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Skeleton variant="rounded" animation="wave" height={320} />
          <Skeleton variant="rounded" animation="wave" height={280} />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Paper variant="lendia_paper">
            <SectionHeader label="Ingresos por servicio" first />
            <Typography variant="body2" color="tokens.textTertiary" sx={{ mt: 0.5, mb: 2, lineHeight: 1.5 }}>
              Selecciona la categoría y captura volumen y precio. Agrega un bloque por cada servicio con tarifa
              distinta.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {values.services.map((service, index) => (
                <CardService
                  key={service.id}
                  service={service}
                  categories={categories}
                  n={index + 1}
                  onChange={(patch) => updateService(index, patch)}
                  onRemove={() => removeService(service.id)}
                  canRemove={values.services.length > 1}
                  testId={`perfil-ingresos-servicio-${index + 1}`}
                />
              ))}
            </Box>
            <ButtonAddService onClick={addService} testId="perfil-ingresos-agregar-servicio-button" />
            <BarTotal label="Total de ingresos mensuales" value={formatCurrencyWhole(totalIncome)} />
          </Paper>

          <Paper variant="lendia_paper">
            <SectionHeader label="Egresos mensuales" first />
            <Typography variant="body2" color="tokens.textTertiary" sx={{ mt: 0.5, mb: 2, lineHeight: 1.5 }}>
              Captura el monto promedio mensual de cada concepto. Si alguno no aplica, déjalo en cero.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {values.expenses.map((expense, index) => (
                <RowExpense
                  key={expense.id}
                  expense={expense}
                  n={index + 1}
                  totalIncome={totalIncome}
                  onChange={(amount) => setFieldValue(`expenses[${index}].amount`, amount)}
                  testId={`perfil-ingresos-gasto-${index + 1}-input`}
                />
              ))}
            </Box>
            <BarTotal label="Total de egresos mensuales" value={formatCurrencyWhole(totalExpenses)} />
          </Paper>

          <Paper variant="lendia_paper">
            <SectionHeader label="Utilidad mensual" first />
            <ProfitSummary totalIncome={totalIncome} totalExpenses={totalExpenses} />
          </Paper>

          <Paper variant="lendia_paper">
            <SectionHeader label="Declaración" first />
            <Typography
              variant="body3"
              component="p"
              color="tokens.textTertiary"
              sx={{ mt: 0.5, mb: 1.75, lineHeight: 1.65 }}
            >
              Declaro bajo protesta de decir verdad que la información y los datos contenidos en esta solicitud son
              verdaderos, exactos y reflejan de manera fehaciente mis ingresos a la fecha de su emisión. Reconozco que
              forma parte integrante de la solicitud presentada ante Mercader Financial y autorizo su uso como elemento
              de análisis, evaluación y documentación del procedimiento correspondiente. Me obligo a proporcionar la
              documentación comprobatoria que respalde lo aquí manifestado, así como a notificar cualquier modificación
              sustancial a dicha información.
            </Typography>
            <Checkbox
              checked={values.declara}
              onChange={(checked) => setFieldValue("declara", checked)}
              label="Acepto la declaración y confirmo que las cifras son correctas"
              disabled={bloqueado}
            />
          </Paper>
        </Box>
      )}

      <StepActions
        // Regreso según `status_config`; si no lo trae, a "Tu práctica médica".
        onBack={() => navigate(previousStatus?.url || PATHS_TOFU_V2.registroDatosPersonales.path)}
        onNext={formik.submitForm}
        nextDisabled={!formik.isValid || totalIncome <= 0 || hasIncompleteService || bloqueado}
        backTestId="perfil-ingresos-back-button"
        nextTestId="perfil-ingresos-continue-button"
      />
    </Box>
  );
};
